import { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../context/AuthProvider';
import { apiFetch } from '../../lib/api';
import {
  USER_PROFILE_UPDATED_EVENT,
  type UserProfileUpdatedDetail,
} from '../../lib/userProfileEvents';
import PortalAvatar from '../profile/PortalAvatar';
import { cyberGlassTextMuted, cyberGlassTextPrimary } from '../../styles/cyberGlass';

type MeSummary = {
  email?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  title?: string | null;
  agentTitle?: string | null;
  photoURL?: string | null;
};

const openKey = keyframes`
  0%   { transform: scaleY(0); opacity: 0; }
  60%  { transform: scaleY(1.06); }
  100% { transform: scaleY(1); opacity: 1; }
`;

const closeKey = keyframes`
  0%   { transform: scaleY(1); opacity: 1; }
  40%  { transform: scaleY(1.04); }
  100% { transform: scaleY(0); opacity: 0; }
`;

const Wrapper = styled.div`
  width: 100%;
`;

const IdentityToggle = styled.button<{ $collapsed: boolean }>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  display: grid;
  grid-template-columns: ${({ $collapsed }) => ($collapsed ? '1fr' : 'auto minmax(0, 1fr)')};
  align-items: center;
  gap: ${({ $collapsed }) => ($collapsed ? '0' : '10px')};
  cursor: pointer;
  color: ${cyberGlassTextPrimary};
`;

const IdentityText = styled.div`
  min-width: 0;
  display: grid;
  gap: 2px;
`;

const TitleLine = styled.div`
  min-width: 0;
  font-size: 0.68rem;
  line-height: 1.1;
  font-weight: 900;
  letter-spacing: 0.03em;
  color: ${cyberGlassTextMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
`;

const NameLine = styled.div`
  min-width: 0;
  font-size: 0.88rem;
  line-height: 1.2;
  font-weight: 500;
  color: ${cyberGlassTextPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Menu = styled.div<{ $open: boolean }>`
  transform-origin: bottom;
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.75rem;
  animation: ${({ $open }) => ($open ? openKey : closeKey)} 0.28s
    cubic-bezier(0.22, 1.44, 0.46, 0.99);
`;

const Btn = styled.button`
  background: #3e7fe8;
  border: none;
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

interface Props {
  collapsed: boolean;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function emailLocalPart(email: string): string {
  const local = asText(email).split('@')[0]?.trim();
  return local || 'User';
}

function normalizeRole(value: unknown): 'admin' | 'agent' | 'client' | 'candidate' | null {
  const role = asText(value).toLowerCase();
  if (role === 'admin' || role === 'agent' || role === 'client' || role === 'candidate') {
    return role;
  }
  return null;
}

export default function AccountMenuBubble({ collapsed }: Props) {
  const { user, role, logout } = useAuth() as any;
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<MeSummary | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      return;
    }

    let cancelled = false;
    const loadProfile = () => {
      apiFetch<MeSummary>('/api/users/me')
        .then((data) => {
          if (!cancelled) setProfile(data ?? null);
        })
        .catch(() => {
          if (!cancelled) setProfile(null);
        });
    };

    const onProfileUpdated = (event: Event) => {
      const custom = event as CustomEvent<UserProfileUpdatedDetail>;
      const detail = custom.detail ?? null;
      if (!detail) {
        loadProfile();
        return;
      }
      if (!cancelled) {
        setProfile((prev) => ({
          ...(prev ?? {}),
          ...detail,
        }));
      }
    };

    loadProfile();
    window.addEventListener(USER_PROFILE_UPDATED_EVENT, onProfileUpdated as EventListener);

    return () => {
      cancelled = true;
      window.removeEventListener(USER_PROFILE_UPDATED_EVENT, onProfileUpdated as EventListener);
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!open) return;
    const cb = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', cb);
    return () => document.removeEventListener('mousedown', cb);
  }, [open]);

  const email = asText(profile?.email) || asText(user?.email);
  const firstName = asText(profile?.firstName);
  const lastName = asText(profile?.lastName);
  const combinedName = `${firstName} ${lastName}`.trim();
  const displayName = combinedName || asText(profile?.displayName) || emailLocalPart(email);
  const resolvedRole = normalizeRole(role) || normalizeRole(profile?.role);
  const staffTitle = asText(profile?.title) || asText(profile?.agentTitle);
  const displayTitle =
    resolvedRole === 'admin'
      ? staffTitle || 'Admin'
      : resolvedRole === 'agent'
      ? staffTitle || 'Agent'
      : resolvedRole === 'client'
      ? 'Client'
      : resolvedRole === 'candidate'
      ? 'Candidate'
      : 'Member';
  const photoURL = asText(profile?.photoURL) || asText(user?.photoURL) || null;

  return (
    <Wrapper ref={ref}>
      <IdentityToggle
        type="button"
        $collapsed={collapsed}
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Open account menu"
      >
        <PortalAvatar
          uid={user?.uid ?? null}
          email={email || null}
          firstName={asText(profile?.firstName) || null}
          lastName={asText(profile?.lastName) || null}
          name={displayName}
          photoURL={photoURL}
          size={34}
          disableNavigation
        />
        {!collapsed ? (
          <IdentityText>
            <TitleLine>{displayTitle}</TitleLine>
            <NameLine>{displayName}</NameLine>
          </IdentityText>
        ) : null}
      </IdentityToggle>

      <Menu $open={open}>
        <Btn onClick={logout}>Logout</Btn>
      </Menu>
    </Wrapper>
  );
}
